import React, { useEffect, useMemo, useState } from "react";
import { faTruck } from "@fortawesome/free-solid-svg-icons";
import { getDeploymentSummary } from "../../../Apis/DashboardApis/primaryDashboardApi";
import CardLoader from "../../../Common/ShimmerLoader/CardLoader";
import DashboardSectionCard from "./shared/DashboardSectionCard";
import DashboardFilters from "./shared/DashboardFilters";
import PlannedActualCard from "./shared/PlannedActualCard";
import useDashboardFilters from "./shared/useDashboardFilters";

const DEPLOYMENT_COLORS = {
  primary: "#16a34a",
  secondary: "#ea580c",
};

const formatCount = (value) => {
  if (value === null || value === undefined || value === "") {
    return "-";
  }

  return Number(value).toLocaleString();
};

const buildCards = (deployments) => {
  const list = Array.isArray(deployments) ? deployments : [];

  return list.map((item) => ({
    icon: faTruck,
    title: item?.label || item?.type || "Deployment",
    planned: formatCount(item?.plannedCount),
    actual: formatCount(item?.actualCount),
    color: DEPLOYMENT_COLORS[item?.type] || "#2563eb",
    showIconCircle: false,
  }));
};

const DeploymentSummary = ({ wards, data }) => {
  const {
    filters,
    appliedFilters,
    handleFilterChange,
    handleSearch,
  } = useDashboardFilters();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(!data);

  useEffect(() => {
    if (Array.isArray(data)) {
      return undefined;
    }

    let isMounted = true;

    const fetchSummary = async () => {
      try {
        setLoading(true);
        const response = await getDeploymentSummary(appliedFilters);

        if (isMounted && response?.status) {
          setSummary(response?.data ?? null);
        }
      } catch (error) {
        console.error("Failed to load deployment summary:", error);
        if (isMounted) {
          setSummary(null);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchSummary();

    return () => {
      isMounted = false;
    };
  }, [appliedFilters, data]);

  const cards = useMemo(() => {
    if (Array.isArray(data)) {
      return data;
    }

    return buildCards(summary?.deployments);
  }, [data, summary]);

  return (
    <DashboardSectionCard
      title="Deployment Summary"
      className="pd-deployment-section"
      filters={
        <DashboardFilters
          className="pd-deployment-filters"
          searchVariant="inline"
          showDates={false}
          ward={filters?.ward}
          wards={wards}
          onWardChange={(value) => handleFilterChange("ward", value)}
          onSearch={handleSearch}
          showSearch
        />
      }
    >
      {loading ? (
        <CardLoader count={2} />
      ) : (
        <div className="pd-metric-row cols-2 pd-deployment-row">
          {cards.map((item) => (
            <PlannedActualCard key={item.title} {...item} />
          ))}
        </div>
      )}
    </DashboardSectionCard>
  );
};

export default DeploymentSummary;
